import {
  onCleanup,
  createEffect,
  createRoot,
  onMount,
  createSignal,
} from 'https://cdn.jsdelivr.net/npm/solid-js@1.7.3/+esm';
import { insert } from 'https://cdn.jsdelivr.net/npm/solid-js@1.7.3/web/+esm';
import { register, compose } from 'https://cdn.jsdelivr.net/npm/component-register@0.6.7/+esm';

// Utility custom element functions

function createProps(raw) {
  const keys = Object.keys(raw);
  const props = {};
  for (let i = 0; i < keys.length; i++) {
    const [get, set] = createSignal(raw[keys[i]]);
    Object.defineProperty(props, keys[i], {
      get() {
        return get();
      },
      set(v) {
        set(() => v);
      },
    });
  }
  return props;
}

function lookupContext(el) {
  if (el.assignedSlot && el.assignedSlot._$owner) return el.assignedSlot._$owner;
  let next = el.parentNode;
  while (next && !next._$owner && !(next.assignedSlot && next.assignedSlot._$owner)) next = next.parentNode;
  return next && next.assignedSlot ? next.assignedSlot._$owner : el._$owner;
}

function withSolid(ComponentType) {
  return (rawProps, options) => {
    const { element } = options;
    return createRoot((dispose) => {
      const props = createProps(rawProps);
      element.addPropertyChangedCallback((key, val) => (props[key] = val));
      element.addReleaseCallback(() => {
        element.renderRoot.textContent = '';
        dispose();
      });
      const comp = ComponentType(props, options);
      return insert(element.renderRoot, comp);
    }, lookupContext(element));
  };
}

function withNoShadowDOM(ComponentType = (props, options) => {}) {
  return (props, options) => {
    const { element } = options;
    element.renderRoot = element;
    return ComponentType(props, options);
  };
}

const customShadowlessElement = (tagName, props = {}, Component = (props, options) => {}, ...rest) =>
  compose(register(tagName, props), withNoShadowDOM, withSolid, ...rest)(Component);

function QuantityInput(props, { element }) {
  const input = element.querySelector('input');
  const changeEvent = new Event('change', { bubbles: true });

  element.querySelectorAll('button').forEach((button) => {
    button.addEventListener('click', onButtonClick);
  });

  createEffect(() => {
    if (props.value !== input.value) return;
    element.setAttribute('value', input.value);
    input.value = props.value;
  });

  function constrainedInputValue() {
    const value = parseInt(input.value);

    let min = -Infinity,
      max = Infinity;

    if (input.min) min = parseInt(input.min);
    if (input.max) max = parseInt(input.max);

    return Math.min(Math.max(value, min), max);
  }

  function validateQtyRules() {
    const value = parseInt(input.value);
    if (input.min) {
      const min = parseInt(input.min);
      const buttonMinus = element.querySelector("button[name='decrement']");
      buttonMinus.toggleAttribute('disabled', value <= min);
    }
    if (input.max) {
      const max = parseInt(input.max);
      const buttonPlus = element.querySelector("button[name='increment']");
      buttonPlus.toggleAttribute('disabled', value >= max);
    }
  }

  function onButtonClick(event) {
    event.preventDefault();
    const previousValue = input.value;

    event.target.name === 'increment' ? input.stepUp() : input.stepDown();
    if (previousValue !== input.value) {
      input.dispatchEvent(new Event('change', { bubbles: true, cancelable: true }));
    }
  }

  onMount(() => {
    validateQtyRules();

    function onInputChange(event) {
      input.value = constrainedInputValue();
      element.setAttribute('value', input.value);
      validateQtyRules();
    }

    input.addEventListener('change', onInputChange);

    onCleanup(() => {
      input.removeEventListener('change', onInputChange);
    });
  });
}

customShadowlessElement('quantity-input', { value: 1 }, QuantityInput);
